import { useState, useEffect, useMemo, useRef } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useParams, useLocation } from "wouter";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandInput, CommandList, CommandItem, CommandEmpty } from "@/components/ui/command";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Clapperboard, Users, Package, Video, Sparkles, BookOpen, UserCircle, Film,
  Clock, Check, ChevronDown, ChevronUp, Wand2, Search, Trash2,
  Loader2, AlertTriangle, Ban, CheckSquare, Square, Download, Bookmark,
  Play, Pencil, type LucideIcon,
} from "lucide-react";
import type {
  Persona, Product, Offer, NarrativeAngle, PersonaCharacter, StoryCategory,
  VideoExpressStyleRecord, VideoExpressCampaign, VideoExpressScript,
  VideoExpressRender, VideoExpressScriptPlan,
} from "@shared/schema";
import { VIDEO_EXPRESS_STYLES, type VideoExpressStyle } from "@shared/video-express-styles";
import { apiRequest, invalidateGeneratedAdListings, queryClient } from "@/lib/queryClient";
import {
  buildVideoExpressPayload,
  buildVideoExpressSyncPayload,
  extractServerError,
  type VideoExpressInput,
  type VideoExpressAngleMode,
} from "@/lib/video-express-create";
import {
  PHASE_META, PHASE_ORDER, PREP_STAGE_DESCRIPTION, computeScriptState, storyboardSlots, isActive,
  type AutopilotStatus, type Phase,
} from "@/lib/video-express-pipeline";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import { PastRunsSection } from "@/components/past-runs-section";

const AD_LENGTHS = [36, 72, 108];

/**
 * Video Express — single-page UX (mirrors Ads Express). Inputs (persona,
 * product, story category, narrative angle, ad length, cast) sit at the top, a
 * multi-select video-styles grid follows, one Generate button fans each selected
 * style out into its own campaign via the shared create → PATCH sync →
 * start-autopilot flow, and a live results area at the bottom tracks every
 * campaign's pipeline. The per-campaign detail route handles deep editing.
 */

/**
 * A style-tile preview image with a graceful fallback: if the style has no
 * image, or the URL fails to load in the browser, we render a muted placeholder
 * with a film icon instead of leaving a blank square.
 */
function StyleTilePreview({ imageUrl, styleValue }: { imageUrl?: string | null; styleValue: string }) {
  const [errored, setErrored] = useState(false);
  // Recover after a transient load failure: if the URL changes, retry the image.
  useEffect(() => { setErrored(false); }, [imageUrl]);
  const showImage = !!imageUrl && !errored;
  if (showImage) {
    return (
      <img
        src={imageUrl!}
        alt=""
        onError={() => setErrored(true)}
        className="w-full aspect-square rounded-md object-cover mb-1.5 border border-border/40"
        loading="lazy"
        data-testid={`img-video-style-${styleValue.toLowerCase()}`}
      />
    );
  }
  return (
    <div
      className="w-full aspect-square rounded-md mb-1.5 border border-border/40 bg-muted flex items-center justify-center"
      data-testid={`placeholder-video-style-${styleValue.toLowerCase()}`}
    >
      <Film className="h-5 w-5 text-muted-foreground/50" />
    </div>
  );
}

export default function VideoExpressCampaignsPage() {
  const { brandId } = useParams<{ brandId: string }>();
  const { toast } = useToast();

  // ── Inputs ────────────────────────────────────────────────────────────────
  const [personaId, setPersonaId] = useState<string | null>(null);
  const [productId, setProductId] = useState<string | null>(null);
  // A campaign promotes EITHER a product OR an offer (mirrors UGC Express).
  const [promotedItemType, setPromotedItemType] = useState<"product" | "offering">("product");
  const [offeringId, setOfferingId] = useState<string | null>(null);
  const [narrativeAngleId, setNarrativeAngleId] = useState<string | null>(null);
  const [selectedCharacterIds, setSelectedCharacterIds] = useState<string[]>([]);
  const [storyCategory, setStoryCategory] = useState<string>("");
  // Ad length: a longer ad renders MORE 9-panel storyboards (36s = 1, 72s = 2,
  // 108s = 3), each a fixed 36s of 4s clips.
  const [clipTotalSeconds, setClipTotalSeconds] = useState(36);
  const [angleMode, setAngleMode] = useState<VideoExpressAngleMode>("generate");
  const [concept, setConcept] = useState("");
  const [conceptSettled, setConceptSettled] = useState("");
  // Multi-select video styles — each chosen style fans out into its own video.
  const [selectedStyles, setSelectedStyles] = useState<Set<string>>(new Set());

  const [generating, setGenerating] = useState(false);
  const generatingRef = useRef(false);
  const resultsRef = useRef<HTMLDivElement>(null);
  const narrativeAngleRef = useRef<HTMLDivElement>(null);
  const [deleteTarget, setDeleteTarget] = useState<VideoExpressCampaign | null>(null);

  // Session scoping: the bottom "Your videos" results show ONLY campaigns
  // launched from THIS browser-tab session — not the brand's whole VideoExpress
  // history. We persist the ids in sessionStorage keyed by brand so an
  // accidental refresh keeps the in-flight runs visible (the autopilot keeps
  // running server-side regardless of the page).
  const sessionKey = `ve-session-campaigns:${brandId}`;
  const [sessionCampaignIds, setSessionCampaignIds] = useState<string[]>(() => {
    if (typeof window === "undefined") return [];
    try {
      const raw = window.sessionStorage.getItem(`ve-session-campaigns:${brandId}`);
      return raw ? (JSON.parse(raw) as string[]) : [];
    } catch {
      return [];
    }
  });
  const rememberSessionCampaigns = (ids: string[]) => {
    if (ids.length === 0) return;
    // Each Generate click REPLACES the tracked batch — we only ever show the
    // campaigns from the most recent click, never a union with earlier ones.
    // The replacement is persisted so an in-flight refresh keeps this batch.
    const next = Array.from(new Set(ids));
    setSessionCampaignIds(next);
    try { window.sessionStorage.setItem(sessionKey, JSON.stringify(next)); } catch {}
  };
  const forgetSessionCampaign = (campaignId: string) => {
    setSessionCampaignIds((prev) => {
      const next = prev.filter((x) => x !== campaignId);
      try { window.sessionStorage.setItem(sessionKey, JSON.stringify(next)); } catch {}
      return next;
    });
  };

  // Live video styles — master-managed library is the source of truth. The
  // hard-coded array is only a genuine last-resort fallback (most of its tiles
  // have no preview image). The global query defaults (retry:false +
  // staleTime:Infinity) would cache a transient first-paint 401 forever and wedge
  // this query empty, so override them: retry the auth race and re-fetch on mount.
  const { data: liveVideoStyles, isLoading: loadingStyles } = useQuery<VideoExpressStyleRecord[]>({
    queryKey: ["/api/video-express-styles"],
    retry: 3,
    retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 8000),
    staleTime: 30_000,
    refetchOnMount: true,
  });
  const hasLiveStyles = !!liveVideoStyles && liveVideoStyles.length > 0;
  // While the live list is loading (and none is cached yet), hold off rendering
  // the stale hard-coded set — show a loading state instead of blank tiles.
  const VIDEO_STYLES: VideoExpressStyleRecord[] | VideoExpressStyle[] = hasLiveStyles
    ? liveVideoStyles!
    : VIDEO_EXPRESS_STYLES.filter((s) => s.status !== "archived");

  const { data: campaigns, isLoading: loadingCampaigns } = useQuery<VideoExpressCampaign[]>({
    queryKey: ["/api/brands", brandId, "video-express-campaigns"],
    queryFn: async () => {
      const res = await fetch(`/api/brands/${brandId}/video-express-campaigns`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch VideoExpress campaigns");
      return res.json();
    },
    enabled: !!brandId,
  });

  const { data: personas } = useQuery<Persona[]>({
    queryKey: [`/api/brands/${brandId}/personas`],
    enabled: !!brandId,
    staleTime: 30_000,
  });

  const { data: products } = useQuery<Product[]>({
    queryKey: [`/api/brands/${brandId}/products`],
    enabled: !!brandId,
    staleTime: 30_000,
  });

  const { data: offers } = useQuery<Offer[]>({
    queryKey: [`/api/brands/${brandId}/offers`],
    enabled: !!brandId,
    staleTime: 30_000,
  });

  const { data: personaCharacters, isLoading: loadingCharacters } = useQuery<PersonaCharacter[]>({
    queryKey: [`/api/personas/${personaId}/characters`],
    enabled: !!personaId,
  });

  // Reset the cast whenever the persona changes.
  useEffect(() => { setSelectedCharacterIds([]); }, [personaId]);

  // In offer mode angles are scoped by offering_id; in product mode by
  // product_id (product behavior byte-for-byte unchanged).
  const isOfferMode = promotedItemType === "offering";
  const promotedItemId = isOfferMode ? offeringId : productId;

  const { data: narrativeAngles, isLoading: loadingAngles } = useQuery<NarrativeAngle[]>({
    queryKey: [`/api/brands/${brandId}/narrative-angles`, { persona_id: personaId, product_id: productId, offering_id: offeringId }],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (personaId) params.set("persona_id", personaId);
      if (isOfferMode) {
        if (offeringId) params.set("offering_id", offeringId);
      } else if (productId) {
        params.set("product_id", productId);
      }
      const res = await fetch(`/api/brands/${brandId}/narrative-angles?${params}`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch narrative angles");
      return res.json();
    },
    enabled: !!brandId && !!personaId && !!promotedItemId,
  });

  const { data: storyCategoryLibrary, isLoading: loadingStoryCategories } = useQuery<StoryCategory[]>({
    queryKey: ["/api/story-categories"],
  });
  const storyCategoryOptions = useMemo(
    () =>
      (storyCategoryLibrary || [])
        .map((c) => ({
          value: c.name,
          label: c.name,
          family: c.familyTags?.[0]?.trim() || "",
          description: c.description?.trim() || "",
          imageUrl: c.imageUrl ?? null,
        }))
        .sort((a, b) =>
          (a.family || "").localeCompare(b.family || "") ||
          a.label.localeCompare(b.label),
        ),
    [storyCategoryLibrary],
  );

  const selectedPersona = personaId ? personas?.find((p) => p.id === personaId) : null;
  const selectedProduct = productId ? products?.find((p) => p.id === productId) : null;
  const selectedOffering = offeringId ? offers?.find((o) => o.id === offeringId) : null;
  const activeAngles = (narrativeAngles || []).filter((a) => !a.archivedAt);

  // Mark the custom concept "settled" ~900ms after the last keystroke so Generate
  // only unlocks once the user has stopped typing (a custom concept is
  // transformed into a saved angle once per campaign, irreversibly).
  useEffect(() => {
    const t = setTimeout(() => setConceptSettled(concept), 900);
    return () => clearTimeout(t);
  }, [concept]);

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiRequest("DELETE", `/api/video-express-campaigns/${id}`);
    },
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: ["/api/brands", brandId, "video-express-campaigns"] });
      forgetSessionCampaign(id);
      toast({ title: "VideoExpress campaign deleted" });
      setDeleteTarget(null);
    },
    onError: (e: any) => {
      toast({ title: "Failed to delete campaign", description: e.message, variant: "destructive" });
    },
  });

  // ── Gating ──────────────────────────────────────────────────────────────
  const conceptDoneTyping = conceptSettled === concept && !!concept.trim();
  const angleReady =
    angleMode === "saved"
      ? !!narrativeAngleId
      : angleMode === "generate"
        ? true
        : conceptDoneTyping;

  // A cast-less campaign is a dead end on the detail page, so require ≥1 cast
  // member whenever the persona has characters. Only waive when the characters
  // query has actually resolved to an empty array (never on a loading/errored
  // fetch).
  const castReady =
    !loadingCharacters &&
    (selectedCharacterIds.length > 0 ||
      (Array.isArray(personaCharacters) && personaCharacters.length === 0));

  const inputsReady = !!personaId && !!promotedItemId && !!storyCategory && angleReady && castReady;
  const canGenerate = inputsReady && selectedStyles.size > 0;

  // Derive a stable base name from the chosen persona × product; each launched
  // style gets the style label appended so the campaigns are distinguishable.
  function baseName(): string {
    const parts = [selectedPersona?.name, (isOfferMode ? selectedOffering?.name : selectedProduct?.name)].filter(Boolean) as string[];
    return parts.length > 0 ? parts.join(" × ") : "VideoExpress";
  }

  function inputForStyle(styleValue: string, styleLabel: string): VideoExpressInput {
    return {
      name: `${baseName()} · ${styleLabel}`,
      personaId,
      promotedItemType,
      productId,
      offeringId,
      angleMode,
      narrativeAngleId,
      concept,
      selectedCharacterIds,
      videoStyle: styleValue,
      storyCategory,
      clipTotalSeconds,
    };
  }

  const styleLabelByValue = useMemo(() => {
    const m = new Map<string, string>();
    for (const s of VIDEO_STYLES as { value: string; label: string }[]) m.set(s.value, s.label);
    return m;
  }, [VIDEO_STYLES]);

  // ── Fan-out launch ──────────────────────────────────────────────────────
  // For each selected style: create → flush sync → start-autopilot, mirroring
  // the single create flow exactly. Each style is independently guarded so one
  // failure never aborts the rest; the server runs each autopilot
  // asynchronously, so start-autopilot returns fast and the styles fan out.
  async function handleGenerate() {
    if (generatingRef.current || !canGenerate) return;
    generatingRef.current = true;
    setGenerating(true);

    // Take the user straight to the "Your videos" results area so they can
    // watch the pipeline kick off as the rows stream in below.
    requestAnimationFrame(() => {
      resultsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    });

    const styleValues = Array.from(selectedStyles);
    let launched = 0;
    let failed = 0;
    let firstError = "";
    const createdIds: string[] = [];

    for (const styleValue of styleValues) {
      const label = styleLabelByValue.get(styleValue) || styleValue;
      const input = inputForStyle(styleValue, label);
      try {
        const created = await apiRequest(
          "POST",
          `/api/brands/${brandId}/video-express-campaigns`,
          buildVideoExpressPayload(input),
        ).then((r) => r.json());
        createdIds.push(created.id);
        await apiRequest("PATCH", `/api/video-express-campaigns/${created.id}`, buildVideoExpressSyncPayload(input));
        await apiRequest("POST", `/api/video-express-campaigns/${created.id}/start-autopilot`);
        launched += 1;
      } catch (e: any) {
        failed += 1;
        if (!firstError) firstError = extractServerError(e);
      }
    }

    // Scope the results list to this session: every campaign we created (even
    // ones whose autopilot kickoff failed — the campaign row still exists and
    // is resumable) belongs in "Your videos".
    rememberSessionCampaigns(createdIds);

    generatingRef.current = false;
    setGenerating(false);
    queryClient.invalidateQueries({ queryKey: ["/api/brands", brandId, "video-express-campaigns"] });

    if (failed === 0) {
      setSelectedStyles(new Set());
      toast({
        title: `Generating ${launched} video${launched === 1 ? "" : "s"}`,
        description: "They're running the pipeline below. You can leave this page — it keeps running.",
      });
    } else if (launched === 0) {
      toast({ title: "Couldn't start any videos", description: firstError || "Please try again.", variant: "destructive" });
    } else {
      toast({
        title: `Started ${launched}, ${failed} failed`,
        description: firstError || "Some styles failed to start.",
        variant: "destructive",
      });
    }
  }

  // ── Options ───────────────────────────────────────────────────────────────
  const personaOptions = (personas || []).map((p) => ({
    value: p.id,
    label: p.name,
    imageUrl: (p as any).imageUrl || null,
    description: p.awarenessStage ? p.awarenessStage.replace(/_/g, " ") : (p.summary || null),
  }));

  const productOptions = (products || []).map((p) => {
    const imgs = Array.isArray(p.adgenImageRefs) ? (p.adgenImageRefs as string[]) : [];
    return {
      value: p.id,
      label: p.name,
      imageUrl: imgs[0] || null,
      description: p.category || p.summary || p.description || null,
    };
  });

  const offerOptions = (offers || []).map((o) => {
    const imgs = Array.isArray((o as any).adgenImageRefs) ? ((o as any).adgenImageRefs as string[]) : [];
    return {
      value: o.id,
      label: o.name,
      imageUrl: imgs[0] || null,
      description: (o as any).shortDescription || (o as any).summary || (o as any).offerCategory || null,
    };
  });

  const angleOptions = activeAngles.map((a) => ({
    value: a.id,
    label: a.name,
    imageUrl: null,
    description: a.family ? `${a.family}${a.summary ? " · " + a.summary : ""}` : (a.summary || null),
  }));

  const personaMap = useMemo(() => new Map((personas || []).map((p) => [p.id, p])), [personas]);
  const productMap = useMemo(() => new Map((products || []).map((p) => [p.id, p])), [products]);
  const offeringMap = useMemo(() => new Map((offers || []).map((o) => [o.id, o])), [offers]);

  const allStyleValues = (VIDEO_STYLES as { value: string }[]).map((s) => s.value);
  const allSelected = allStyleValues.length > 0 && selectedStyles.size === allStyleValues.length;
  const toggleStyle = (value: string) =>
    setSelectedStyles((prev) => {
      const next = new Set(prev);
      if (next.has(value)) next.delete(value);
      else next.add(value);
      return next;
    });

  const sessionIdSet = useMemo(() => new Set(sessionCampaignIds), [sessionCampaignIds]);
  const sortedCampaigns = useMemo(
    () =>
      [...(campaigns || [])]
        // Only campaigns launched in this session — no brand-wide fallback.
        .filter((c) => sessionIdSet.has(c.id))
        .sort((a, b) => {
          const ta = a.createdAt ? new Date(a.createdAt as any).getTime() : 0;
          const tb = b.createdAt ? new Date(b.createdAt as any).getTime() : 0;
          return tb - ta;
        }),
    [campaigns, sessionIdSet],
  );

  // Past runs (Task #1633): every brand campaign NOT in the current session's
  // batch, newest first. Server-backed, so history persists across browser
  // sessions — no more sessionStorage-only memory.
  const pastCampaigns = useMemo(
    () =>
      [...(campaigns || [])]
        .filter((c) => !sessionIdSet.has(c.id))
        .sort((a, b) => {
          const ta = a.createdAt ? new Date(a.createdAt as any).getTime() : 0;
          const tb = b.createdAt ? new Date(b.createdAt as any).getTime() : 0;
          return tb - ta;
        }),
    [campaigns, sessionIdSet],
  );

  return (
    <>
      <div className="max-w-5xl mx-auto px-8 py-6 space-y-5">
        <div className="flex items-start gap-3 group">
          <Clapperboard className="h-6 w-6 text-violet-500 shrink-0 mt-0.5" />
          <div>
            <h1 className="text-2xl font-bold tracking-tight" data-testid="text-video-express-campaigns-title">
              AI VideoExpress
            </h1>
            <p className="text-sm text-muted-foreground mt-0.5">
              Set your inputs, pick one or more styles, and generate a video per style — each runs the full pipeline below.
            </p>
          </div>
        </div>

        {/* Inputs */}
        <Card className="border-border/40">
          <CardContent className="p-5 space-y-4">
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-start gap-3">
                <div className="h-9 w-9 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0">
                  <Sparkles className="h-4 w-4" />
                </div>
                <div>
                  <p className="text-sm font-semibold">Set your inputs</p>
                  <p className="text-xs text-muted-foreground">Persona and {isOfferMode ? "offer" : "product"} are both required.</p>
                </div>
              </div>
              <div
                className="flex items-center gap-1 p-0.5 rounded-full border border-border/70 bg-background/60 shadow-sm shrink-0"
                data-testid="toggle-promoted-item"
              >
                <Button
                  type="button"
                  size="sm"
                  variant={isOfferMode ? "ghost" : "default"}
                  className="h-7 px-3 text-[11px] gap-1 rounded-full"
                  onClick={() => { setPromotedItemType("product"); setOfferingId(null); setNarrativeAngleId(null); }}
                  data-testid="toggle-promoted-product"
                >
                  <Package className="h-3 w-3" /> Product
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant={isOfferMode ? "default" : "ghost"}
                  className="h-7 px-3 text-[11px] gap-1 rounded-full"
                  onClick={() => { setPromotedItemType("offering"); setProductId(null); setNarrativeAngleId(null); }}
                  data-testid="toggle-promoted-offer"
                >
                  <Sparkles className="h-3 w-3" /> Offer
                </Button>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <VideoExpressPicker
                icon={<Users className="h-4 w-4" />}
                label="Persona"
                placeholder={personaOptions.length === 0 ? "No personas yet" : "Choose persona"}
                value={personaId || ""}
                onChange={(v) => { setPersonaId(v); setNarrativeAngleId(null); }}
                options={personaOptions}
                testId="select-video-express-persona"
                disabled={personaOptions.length === 0}
              />
              {isOfferMode ? (
                <VideoExpressPicker
                  icon={<Sparkles className="h-4 w-4" />}
                  label="Offer"
                  placeholder={offerOptions.length === 0 ? "No offers yet" : "Choose offer"}
                  value={offeringId || ""}
                  onChange={(v) => { setOfferingId(v); setNarrativeAngleId(null); }}
                  options={offerOptions}
                  testId="select-video-express-offer"
                  disabled={offerOptions.length === 0}
                />
              ) : (
                <VideoExpressPicker
                  icon={<Package className="h-4 w-4" />}
                  label="Product"
                  placeholder={productOptions.length === 0 ? "No products yet" : "Choose product"}
                  value={productId || ""}
                  onChange={(v) => { setProductId(v); setNarrativeAngleId(null); }}
                  options={productOptions}
                  testId="select-video-express-product"
                  disabled={productOptions.length === 0}
                />
              )}
            </div>

            <div className="space-y-2 border-t border-border/40 pt-4">
              <div className="flex items-center gap-2">
                <Clock className="h-3.5 w-3.5 text-muted-foreground" />
                <p className="text-xs font-semibold">Ad Length</p>
                <span className="text-[11px] text-muted-foreground">How long the finished ad runs.</span>
              </div>
              <div className="grid grid-cols-3 gap-2">
                {AD_LENGTHS.map((total) => {
                  const active = clipTotalSeconds === total;
                  return (
                    <button
                      key={total}
                      type="button"
                      onClick={() => setClipTotalSeconds(total)}
                      className={[
                        "rounded-xl border px-3 py-2.5 text-left transition-all",
                        active
                          ? "border-primary ring-2 ring-primary/40 bg-primary/5 shadow-sm"
                          : "border-border/50 hover:border-primary/40 hover:shadow-sm",
                      ].join(" ")}
                      data-testid={`button-ad-length-${total}`}
                    >
                      <p className="text-sm font-semibold">{total}s</p>
                      <p className="text-[11px] text-muted-foreground">{total / 4} scenes × 4s each</p>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Story category */}
            <div className="space-y-3 border-t border-border/40 pt-4">
              <div className="flex items-center gap-2">
                <BookOpen className="h-3.5 w-3.5 text-muted-foreground" />
                <p className="text-xs font-semibold">Story Category</p>
                <span className="text-[11px] text-muted-foreground">The storytelling method sent to the Creative Strategist.</span>
              </div>

              {loadingStoryCategories ? (
                <div className="grid grid-cols-2 lg:grid-cols-3 gap-2" data-testid="loading-story-categories">
                  {Array.from({ length: 9 }).map((_, i) => (
                    <div key={i} className="flex items-center gap-2.5 rounded-lg border-2 border-border/50 p-2">
                      <div className="h-10 w-10 shrink-0 rounded-md bg-muted/40 animate-pulse" />
                      <div className="min-w-0 flex-1 space-y-1.5">
                        <div className="h-3 w-2/3 rounded bg-muted/40 animate-pulse" />
                        <div className="h-2.5 w-full rounded bg-muted/30 animate-pulse" />
                        <div className="h-2.5 w-4/5 rounded bg-muted/30 animate-pulse" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : storyCategoryOptions.length === 0 ? (
                <div className="rounded-lg border border-dashed border-border/60 bg-muted/30 p-4 text-xs text-muted-foreground" data-testid="empty-story-categories">
                  No story categories are available yet. Add some in the Story Category library to choose one here.
                </div>
              ) : (
                <StoryCategoryGallery
                  options={storyCategoryOptions}
                  value={storyCategory}
                  onChange={(v) => {
                    setStoryCategory(v);
                    requestAnimationFrame(() => {
                      narrativeAngleRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
                    });
                  }}
                />
              )}
            </div>

            {/* Narrative angle */}
            <div ref={narrativeAngleRef} className="space-y-3 border-t border-border/40 pt-4 scroll-mt-4">
              <div className="flex items-center gap-2">
                <BookOpen className="h-3.5 w-3.5 text-muted-foreground" />
                <p className="text-xs font-semibold">Narrative angle</p>
                <span className="text-[11px] text-muted-foreground">Pick a saved angle, write a custom concept, or let us generate one.</span>
              </div>

              <div className="inline-flex items-center rounded-lg border border-border/60 bg-muted/40 p-0.5">
                <button
                  type="button"
                  onClick={() => setAngleMode("saved")}
                  className={[
                    "inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold transition-colors",
                    angleMode === "saved" ? "bg-background text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground",
                  ].join(" ")}
                  data-testid="button-angle-mode-saved"
                >
                  <BookOpen className="h-3.5 w-3.5" /> Saved angle
                </button>
                <button
                  type="button"
                  onClick={() => setAngleMode("custom")}
                  className={[
                    "inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold transition-colors",
                    angleMode === "custom" ? "bg-background text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground",
                  ].join(" ")}
                  data-testid="button-angle-mode-custom"
                >
                  <Sparkles className="h-3.5 w-3.5" /> Custom concept
                </button>
                <button
                  type="button"
                  onClick={() => setAngleMode("generate")}
                  className={[
                    "inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold transition-colors",
                    angleMode === "generate" ? "bg-background text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground",
                  ].join(" ")}
                  data-testid="button-angle-mode-generate"
                >
                  <Wand2 className="h-3.5 w-3.5" /> Generate
                </button>
              </div>

              {angleMode === "saved" ? (
                <VideoExpressPicker
                  icon={<BookOpen className="h-4 w-4" />}
                  label="Narrative Angle"
                  placeholder={
                    !personaId || !promotedItemId
                      ? `Pick persona & ${isOfferMode ? "offer" : "product"} first`
                      : loadingAngles
                        ? "Loading angles…"
                        : angleOptions.length === 0
                          ? "No saved angles for this pair"
                          : "Choose narrative angle"
                  }
                  value={narrativeAngleId || ""}
                  onChange={(v) => setNarrativeAngleId(v)}
                  options={angleOptions}
                  testId="select-video-express-angle"
                  disabled={!personaId || !promotedItemId || loadingAngles || angleOptions.length === 0}
                />
              ) : angleMode === "generate" ? (
                <div className="rounded-lg border border-dashed border-border/60 bg-muted/30 p-4" data-testid="info-angle-mode-generate">
                  <div className="flex items-start gap-2.5">
                    <Wand2 className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                    <div>
                      <p className="text-sm font-medium">We'll generate the concept for you</p>
                      <p className="text-xs text-muted-foreground mt-0.5">No need to write anything — we'll craft the creative concept and narrative direction from your brand, persona, product, and story category when you generate.</p>
                    </div>
                  </div>
                </div>
              ) : (
                <div>
                  <Label htmlFor="video-express-concept" className="text-sm font-medium">Custom concept</Label>
                  <p className="text-xs text-muted-foreground mb-1.5">Describe the moment, idea, or angle. On generate, we'll transform it into a saved narrative angle for this persona &amp; product.</p>
                  <Textarea
                    id="video-express-concept"
                    placeholder="e.g., A woman realizes her deodorant still works after an intense gym session…"
                    value={concept}
                    onChange={(e) => setConcept(e.target.value)}
                    rows={4}
                    className="resize-none"
                    data-testid="input-video-express-concept"
                  />
                </div>
              )}
            </div>

            {/* Select your models */}
            {personaId && (
              <div className="space-y-3 border-t border-border/40 pt-4">
                <div className="flex items-center gap-2">
                  <UserCircle className="h-3.5 w-3.5 text-muted-foreground" />
                  <p className="text-xs font-semibold">Select your models</p>
                  <span className="text-[11px] text-muted-foreground">
                    {loadingCharacters
                      ? "Loading characters…"
                      : (personaCharacters?.length || 0) === 0
                        ? `No characters yet for ${selectedPersona?.name || "this persona"}. Add some on the persona page.`
                        : `Pick which characters from ${selectedPersona?.name || "this persona"} to feature.`}
                  </span>
                  {selectedCharacterIds.length > 0 && (
                    <Badge variant="secondary" className="ml-auto text-[11px]" data-testid="badge-selected-character-count">
                      {selectedCharacterIds.length} selected
                    </Badge>
                  )}
                </div>

                {!loadingCharacters && (personaCharacters?.length || 0) > 0 && (
                  <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 xl:grid-cols-7 gap-2">
                    {(personaCharacters || []).map((c) => {
                      const isSelected = selectedCharacterIds.includes(c.id);
                      const ready = c.avatarStatus === "ready" && !!c.avatarUrl;
                      return (
                        <button
                          key={c.id}
                          type="button"
                          onClick={() => {
                            setSelectedCharacterIds((prev) =>
                              prev.includes(c.id) ? prev.filter((id) => id !== c.id) : [...prev, c.id]
                            );
                          }}
                          className={[
                            "group relative rounded-xl overflow-hidden border text-left transition-all",
                            isSelected
                              ? "border-primary ring-2 ring-primary/40 shadow-sm"
                              : "border-border/50 hover:border-primary/40 hover:shadow-sm",
                          ].join(" ")}
                          data-testid={`character-card-${c.id}`}
                        >
                          <div className="relative aspect-square bg-muted/40 overflow-hidden">
                            {ready ? (
                              <img
                                src={c.avatarUrl!}
                                alt={c.name}
                                className="absolute inset-0 w-full h-full object-cover block"
                                loading="lazy"
                              />
                            ) : (
                              <div className="absolute inset-0 flex items-center justify-center">
                                <UserCircle className="h-6 w-6 text-muted-foreground/40" />
                              </div>
                            )}
                          </div>
                          <div className="px-1.5 py-1 bg-background">
                            <p className="text-[11px] font-semibold leading-tight break-words" title={c.name}>{c.name}</p>
                          </div>
                          {isSelected && (
                            <div className="absolute top-1 right-1 h-4 w-4 rounded-full bg-primary text-primary-foreground flex items-center justify-center shadow">
                              <Check className="h-2.5 w-2.5" strokeWidth={3} />
                            </div>
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Video styles (multi-select) */}
        <Card className="border-border/40">
          <CardContent className="p-5 space-y-4">
            <div className="flex items-start gap-3">
              <div className="h-9 w-9 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0">
                <Video className="h-4 w-4" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold">Video styles</p>
                <p className="text-xs text-muted-foreground" data-testid="text-selected-style-count">
                  {selectedStyles.size} of {allStyleValues.length} selected · each becomes its own video.
                </p>
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="rounded-full shrink-0"
                onClick={() => setSelectedStyles(allSelected ? new Set() : new Set(allStyleValues))}
                disabled={allStyleValues.length === 0}
                data-testid="button-select-all-styles"
              >
                {allSelected ? <Square className="h-3.5 w-3.5 mr-1.5" /> : <CheckSquare className="h-3.5 w-3.5 mr-1.5" />}
                {allSelected ? "Clear all" : "Select all"}
              </Button>
            </div>

            {loadingStyles && !hasLiveStyles ? (
              <div className="grid grid-cols-4 sm:grid-cols-6 gap-2" data-testid="loading-video-styles">
                {Array.from({ length: 9 }).map((_, i) => (
                  <div key={i} className="rounded-lg border border-border/50 px-2 py-1.5">
                    <Skeleton className="w-full aspect-square rounded-md mb-1.5" />
                    <Skeleton className="h-3 w-3/4 mb-1" />
                    <Skeleton className="h-2.5 w-full" />
                  </div>
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-4 sm:grid-cols-6 gap-2">
                {VIDEO_STYLES.map((style) => {
                  const active = selectedStyles.has(style.value);
                  const imageUrl = (style as { imageUrl?: string | null }).imageUrl;
                  return (
                    <button
                      key={style.value}
                      type="button"
                      onClick={() => toggleStyle(style.value)}
                      aria-pressed={active}
                      className={[
                        "relative rounded-lg border px-2 py-1.5 text-left transition-all",
                        active
                          ? "border-primary ring-2 ring-primary/40 bg-primary/5 shadow-sm"
                          : "border-border/50 hover:border-primary/40 hover:shadow-sm",
                      ].join(" ")}
                      data-testid={`button-video-style-${style.value.toLowerCase()}`}
                    >
                      <StyleTilePreview imageUrl={imageUrl} styleValue={style.value} />
                      <p className="text-xs font-semibold leading-tight">{style.label}</p>
                      <p className="text-[10px] text-muted-foreground leading-tight">{style.description}</p>
                      {active && (
                        <div className="absolute top-1 right-1 h-4 w-4 rounded-full bg-primary text-primary-foreground flex items-center justify-center shadow">
                          <Check className="h-2.5 w-2.5" strokeWidth={3} />
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Generate */}
        <div className="sticky bottom-0 -mx-8 px-8 py-4 bg-background/90 backdrop-blur border-t border-border/40 flex items-center justify-between gap-3 z-10">
          <p className="text-xs text-muted-foreground">
            {!inputsReady
              ? "Pick a persona, product, story category, narrative angle and cast to continue."
              : selectedStyles.size === 0
                ? "Pick at least one video style."
                : `${selectedStyles.size} video${selectedStyles.size === 1 ? "" : "s"} ready to generate.`}
          </p>
          <Button
            className="rounded-full px-6"
            onClick={handleGenerate}
            disabled={!canGenerate || generating}
            data-testid="button-generate-videos"
          >
            {generating ? (
              <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Starting…</>
            ) : (
              <><Film className="h-4 w-4 mr-1.5" /> Generate {selectedStyles.size > 0 ? selectedStyles.size : ""} video{selectedStyles.size === 1 ? "" : "s"}</>
            )}
          </Button>
        </div>

        {/* Results */}
        <div ref={resultsRef} className="space-y-3 pt-2 scroll-mt-20">
          <div className="flex items-center gap-2">
            <Clapperboard className="h-4 w-4 text-violet-500" />
            <h2 className="text-sm font-semibold tracking-tight">Your videos</h2>
            {sortedCampaigns.length > 0 && (
              <Badge variant="secondary" className="text-[11px]" data-testid="badge-campaign-count">
                {sortedCampaigns.length}
              </Badge>
            )}
          </div>

          {loadingCampaigns ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {[1, 2, 3, 4].map((i) => <Skeleton key={i} className="aspect-[9/16] w-full rounded-xl" />)}
            </div>
          ) : sortedCampaigns.length === 0 ? (
            <Card className="border-dashed border-border/50">
              <CardContent className="flex flex-col items-center justify-center py-14 text-center">
                <div className="w-12 h-12 rounded-2xl bg-violet-500/10 flex items-center justify-center mb-4">
                  <Clapperboard className="h-6 w-6 text-violet-500" />
                </div>
                <h3 className="font-semibold tracking-tight mb-1">No videos yet</h3>
                <p className="text-sm text-muted-foreground max-w-sm">
                  Set your inputs above, pick one or more styles, and hit Generate. Each style runs the full pipeline and shows up here live.
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="list-video-express-campaigns">
              {sortedCampaigns.map((campaign) => (
                <CampaignRow
                  key={campaign.id}
                  campaign={campaign}
                  brandId={brandId!}
                  persona={campaign.personaId ? personaMap.get(campaign.personaId) || null : null}
                  product={
                    (campaign as any).promotedItemType === "offering" && (campaign as any).offeringId
                      ? ((offeringMap.get((campaign as any).offeringId) as any) || null)
                      : campaign.productId
                        ? productMap.get(campaign.productId) || null
                        : null
                  }
                  onDelete={() => setDeleteTarget(campaign)}
                />
              ))}
            </div>
          )}
        </div>

        {/* Past runs — persistent brand-wide history below the current
            session's videos (Task #1633). */}
        <PastRunsSection
          title="Past runs"
          count={pastCampaigns.length}
          isLoading={loadingCampaigns}
          testIdPrefix="ve-past-runs"
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 pt-1" data-testid="list-ve-past-campaigns">
            {pastCampaigns.map((campaign) => (
              <CampaignRow
                key={campaign.id}
                campaign={campaign}
                brandId={brandId!}
                persona={campaign.personaId ? personaMap.get(campaign.personaId) || null : null}
                product={
                  (campaign as any).promotedItemType === "offering" && (campaign as any).offeringId
                    ? ((offeringMap.get((campaign as any).offeringId) as any) || null)
                    : campaign.productId
                      ? productMap.get(campaign.productId) || null
                      : null
                }
                onDelete={() => setDeleteTarget(campaign)}
              />
            ))}
          </div>
        </PastRunsSection>
      </div>

      {deleteTarget && (
        <Dialog open onOpenChange={(open) => { if (!open && !deleteMutation.isPending) setDeleteTarget(null); }}>
          <DialogContent data-testid="dialog-delete-video-express-campaign">
            <DialogHeader>
              <DialogTitle>Delete VideoExpress Campaign</DialogTitle>
              <DialogDescription>
                Are you sure you want to delete <strong>{deleteTarget.name}</strong>? This will permanently remove the campaign and all its scripts. This action cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setDeleteTarget(null)} disabled={deleteMutation.isPending} data-testid="button-cancel-delete-videoExpress">
                Cancel
              </Button>
              <Button variant="destructive" onClick={() => deleteMutation.mutate(deleteTarget.id)} disabled={deleteMutation.isPending} data-testid="button-confirm-delete-videoExpress">
                {deleteMutation.isPending ? "Deleting..." : "Delete"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}

// ── Live results row ─────────────────────────────────────────────────────────

type Tone = "running" | "done" | "warn" | "fail" | "idle";

const TONE_BAR: Record<Tone, string> = {
  running: "bg-violet-500",
  done: "bg-emerald-500",
  warn: "bg-amber-500",
  fail: "bg-destructive",
  idle: "bg-muted-foreground/40",
};

// Tone-aware status pill (matches the bar tones) used on every card footer.
const TONE_PILL: Record<Tone, string> = {
  running: "border-violet-500/30 bg-violet-500/15 text-violet-600 dark:text-violet-400",
  done: "border-emerald-500/30 bg-emerald-500/15 text-emerald-600 dark:text-emerald-400",
  warn: "border-amber-500/30 bg-amber-500/15 text-amber-600 dark:text-amber-400",
  fail: "border-destructive/30 bg-destructive/15 text-destructive",
  idle: "border-border bg-muted text-muted-foreground",
};

/**
 * Media-forward player for a finished video tile. Shows the poster frame under a
 * glassy play overlay and only swaps in the native controls once playback has
 * started, so the card isn't dominated by a permanent control bar.
 */
function PlayableVideo({ src, testId }: { src: string; testId: string }) {
  const ref = useRef<HTMLVideoElement>(null);
  const [started, setStarted] = useState(false);
  return (
    <>
      {/* The card body doesn't navigate on click — the video plays in place via
          the poster's play button and native controls. Opening the campaign edit
          page happens only through the deliberate Edit button in the card footer. */}
      <video
        ref={ref}
        src={src}
        controls={started}
        playsInline
        preload="metadata"
        className="absolute inset-0 h-full w-full object-cover"
        data-testid={testId}
        onPlay={() => setStarted(true)}
      />
      {!started && (
        <>
          <span className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/50 via-black/0 to-black/10" />
          <button
            type="button"
            aria-label="Play video"
            onClick={(e) => { e.stopPropagation(); void ref.current?.play().catch(() => {}); }}
            className="group/play absolute left-1/2 top-1/2 flex h-14 w-14 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-white/15 ring-1 ring-white/40 shadow-lg backdrop-blur-md transition-transform duration-200 hover:scale-110"
          >
            <Play className="h-6 w-6 translate-x-0.5 fill-white text-white" />
          </button>
        </>
      )}
    </>
  );
}

// Prep stages precede the per-script phases; together they form the full
// 0→100% scale for the campaign-level bar.
const STAGE_ORDER = ["strategy", "concept", ...PHASE_ORDER] as const;
function stagePercent(stage: string): number {
  if (stage === "done") return 100;
  const idx = STAGE_ORDER.indexOf(stage as any);
  if (idx < 0) return 0;
  return Math.round((idx / (STAGE_ORDER.length - 1)) * 100);
}

type CampaignProgress = { label: string; percent: number; tone: Tone; description?: string };

function computeCampaignProgress(
  campaign: VideoExpressCampaign | undefined,
  scripts: VideoExpressScript[] | undefined,
  rendersByScript: Record<string, VideoExpressRender> | undefined,
  plans: VideoExpressScriptPlan[] | undefined,
): CampaignProgress {
  const status = (campaign as any)?.autopilotStatus as AutopilotStatus;

  let stageLabel = "Preparing";
  // Plain-language explanation of what the current stage is actually doing,
  // surfaced under the stage label while a run is active. Copy lives in the
  // shared pipeline module so it stays in sync with the stage labels.
  let stageDescription: string | undefined;
  let pct = 0;
  const list = scripts || [];
  if (list.length === 0) {
    const strategyStatus = (campaign as any)?.creativeStrategyStatus as string | undefined;
    if (strategyStatus !== "ready") {
      stageLabel = strategyStatus === "failed" ? "Creative strategy failed" : "Creative strategy";
      stageDescription = strategyStatus === "failed" ? undefined : PREP_STAGE_DESCRIPTION.strategy;
      pct = stagePercent("strategy");
    } else {
      const videoCount = (campaign as any)?.videoCount || 1;
      const conceptCount = (plans || []).length;
      if (conceptCount < videoCount) {
        stageLabel = "Concepts";
        stageDescription = PREP_STAGE_DESCRIPTION.concept;
        pct = stagePercent("concept");
      } else {
        stageLabel = "Writing script";
        stageDescription = PHASE_META.script.description;
        pct = stagePercent("script");
      }
    }
  } else {
    const slots = storyboardSlots(campaign);
    const videoCount = (campaign as any)?.videoCount || 1;
    let minIdx = PHASE_ORDER.length - 1;
    for (const s of list) {
      const st = computeScriptState(s, rendersByScript?.[s.id], slots);
      const idx = PHASE_ORDER.indexOf(st.phase);
      if (idx < minIdx) minIdx = idx;
    }
    if (list.length < videoCount) minIdx = 0;
    const phase = PHASE_ORDER[minIdx] as Phase;
    stageLabel = PHASE_META[phase].label;
    stageDescription = phase === "done" ? undefined : PHASE_META[phase].description;
    pct = phase === "done" ? 100 : stagePercent(phase);
  }

  switch (status) {
    case "completed":
      return { label: "Ready", percent: 100, tone: "done" };
    case "partial":
      return { label: "Finished with issues", percent: 100, tone: "warn" };
    case "failed":
      return { label: stageLabel ? `Failed at ${stageLabel.toLowerCase()}` : "Failed", percent: pct, tone: "fail" };
    case "cancelled":
      return { label: "Cancelled", percent: pct, tone: "idle" };
    default:
      return { label: stageLabel, percent: pct, tone: "running", description: stageDescription };
  }
}

/**
 * One campaign in the results list. Seeds from the list row (no extra fetch for
 * idle campaigns) and only polls the pipeline queries while the run is active,
 * so completed/old campaigns stay quiet. Stage derivation reuses the shared
 * `video-express-pipeline` helpers so the labels match the detail page exactly.
 */
function CampaignRow({
  campaign: initial, brandId, persona, product, onDelete,
}: {
  campaign: VideoExpressCampaign;
  brandId: string;
  persona: Persona | null;
  product: Product | null;
  onDelete: () => void;
}) {
  const id = initial.id;

  const { data: campaign } = useQuery<VideoExpressCampaign>({
    queryKey: ["/api/video-express-campaigns", id],
    queryFn: async () => {
      const res = await fetch(`/api/video-express-campaigns/${id}`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load campaign");
      return res.json();
    },
    initialData: initial,
    enabled: !!id,
    refetchInterval: (q) => (isActive((q.state.data as any)?.autopilotStatus) ? 4000 : false),
  });

  const active = isActive((campaign as any)?.autopilotStatus);
  // A finished run (completed or partial) still has render output to show, so
  // keep the render query enabled past the active window — otherwise a completed
  // campaign would never surface its final video.
  const status = (campaign as any)?.autopilotStatus as string | undefined;
  const terminalWithOutput = status === "completed" || status === "partial";

  const { data: scripts } = useQuery<VideoExpressScript[]>({
    queryKey: ["/api/video-express-campaigns", id, "scripts"],
    queryFn: async () => {
      const res = await fetch(`/api/video-express-campaigns/${id}/scripts`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load scripts");
      return res.json();
    },
    // Also fetched for finished (completed/partial) campaigns: the Edit entry
    // point below only shows when at least one script actually has rendered
    // scene clips, which requires the scripts payload. Only actively polled
    // while the run is in flight.
    enabled: !!id && (active || terminalWithOutput),
    refetchInterval: () => (active ? 4000 : false),
  });

  const { data: plans } = useQuery<VideoExpressScriptPlan[]>({
    queryKey: ["/api/video-express-campaigns", id, "script-plans"],
    queryFn: async () => {
      const res = await fetch(`/api/video-express-campaigns/${id}/script-plans`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load plans");
      return res.json();
    },
    enabled: !!id && active,
    refetchInterval: () => (active ? 4000 : false),
  });

  const { data: rendersByScript } = useQuery<Record<string, VideoExpressRender>>({
    queryKey: ["/api/video-express-campaigns", id, "renders", "latest"],
    queryFn: async () => {
      const res = await fetch(`/api/video-express-campaigns/${id}/renders/latest`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load renders");
      return res.json();
    },
    enabled: !!id && (active || terminalWithOutput),
    // While the run is active, poll every 4s. Once it goes terminal-with-output
    // (completed/partial), KEEP polling until the final stitched (or captioned)
    // video URL actually lands, then stop. Without this, the campaign-status
    // poll can flip `active` off and clear this query's interval before its own
    // poll catches the now-complete render — leaving the tile stuck on the
    // "Ready" status card with no playable video until a manual reload.
    refetchInterval: (q) => {
      if (active) return 4000;
      if (!terminalWithOutput) return false;
      const rows = q.state.data as Record<string, VideoExpressRender> | undefined;
      const hasFinal = !!rows && Object.values(rows).some(
        (r: any) => r?.status === "complete" && (r?.captionedVideoUrl || r?.outputUrl),
      );
      return hasFinal ? false : 4000;
    },
  });

  const progress = computeCampaignProgress(campaign, scripts, rendersByScript, plans);

  const { toast } = useToast();
  const [, navigate] = useLocation();

  // Edit entry point (deliberately reverses part of the earlier display-only
  // change): finished cards get an explicit Edit button that opens the campaign
  // edit page — where individual scene clips can be edited and the final video
  // rebuilt. It only shows when there's actually something editable there (at
  // least one script with a rendered scene clip), which covers both "Ready" and
  // "Finished with issues" campaigns. The card body itself stays non-navigating.
  const hasEditableClips = (scripts || []).some(
    (s: any) => Array.isArray(s.sceneVideos) && (s.sceneVideos as any[]).some((v: any) => v?.videoUrl),
  );
  const editHref = `/brands/${brandId}/video-express-campaigns/${id}`;

  // Final stitched videos for this campaign (one per script). Prefer the
  // captioned cut once it's ready, else the raw output; only complete renders.
  // We carry the renderId so the per-tile actions (Save into My Ads) can point
  // the server at the exact render to snapshot.
  type FinalVideo = { scriptId: string; renderId: string; url: string };
  const finalVideos = Object.entries(rendersByScript || {})
    .map(([scriptId, r]) => {
      const url = (r as any)?.captionedVideoStatus === "ready" && (r as any)?.captionedVideoUrl
        ? ((r as any).captionedVideoUrl as string)
        : ((r as any)?.outputUrl as string | undefined);
      return r?.status === "complete" && url
        ? { scriptId, renderId: (r as any).id as string, url }
        : null;
    })
    .filter((v): v is FinalVideo => !!v);

  // Track which renders the user has saved into My Ads this session so the
  // button can flip to a "Saved" state (the server is idempotent regardless).
  const [savedRenderIds, setSavedRenderIds] = useState<Set<string>>(new Set());
  const saveMutation = useMutation({
    mutationFn: async (renderId: string) => {
      await apiRequest("POST", `/api/brands/${brandId}/library/save-generated-video`, {
        campaignId: id,
        renderId,
      });
      return renderId;
    },
    onSuccess: (renderId) => {
      setSavedRenderIds((prev) => new Set(prev).add(renderId));
      invalidateGeneratedAdListings();
      toast({ title: "Saved to My Ads", description: "The video is now in your Ad Library." });
    },
    onError: (e: any) => {
      toast({ title: "Couldn't save video", description: extractServerError(e), variant: "destructive" });
    },
  });

  const downloadVideo = (url: string, idx: number) => {
    const a = document.createElement("a");
    a.href = url;
    a.download = `${(campaign as any)?.name || initial.name || "video"}-${idx + 1}.mp4`;
    a.target = "_blank";
    a.rel = "noopener noreferrer";
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const name = (campaign as any)?.name || initial.name;
  const styleLabel = (campaign as any)?.videoStyle || initial.videoStyle;
  const duration = (campaign as any)?.duration || initial.duration;

  const StatusIcon =
    progress.tone === "done" ? Check
      : progress.tone === "fail" ? AlertTriangle
        : progress.tone === "warn" ? AlertTriangle
          : progress.tone === "idle" ? Ban
            : Loader2;
  const iconClass =
    progress.tone === "done" ? "text-emerald-500"
      : progress.tone === "fail" ? "text-destructive"
        : progress.tone === "warn" ? "text-amber-500"
          : progress.tone === "idle" ? "text-muted-foreground"
            : "text-violet-500 animate-spin";

  // Shared meta line (persona / product / style / duration) reused on every tile.
  const metaLine = (
    <div className="flex items-center gap-2 flex-wrap mt-1">
      {persona && (
        <span className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
          <Users className="h-3 w-3 text-violet-500/60" />
          <span className="truncate max-w-[100px]">{persona.name}</span>
        </span>
      )}
      {product && (
        <span className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
          <Package className="h-3 w-3 text-blue-500/60" />
          <span className="truncate max-w-[100px]">{product.name}</span>
        </span>
      )}
      {styleLabel && (
        <span className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
          <Video className="h-3 w-3 text-amber-500/60" />
          <span className="truncate max-w-[100px]">{styleLabel}</span>
        </span>
      )}
      {duration && <span className="text-[11px] text-muted-foreground/60">{duration}</span>}
    </div>
  );

  // The footer (name + status badge + delete) shared by every card. `primary`
  // tiles carry the canonical campaign-level test ids; extra video tiles get a
  // suffixed variant so ids stay unique within a multi-video campaign.
  const renderFooter = (opts: {
    primary: boolean;
    suffix: string;
    badgeLabel: string;
    badgeClassName?: string;
    badgeIcon?: LucideIcon;
    badgeIconSpin?: boolean;
    body?: React.ReactNode;
  }) => {
    const BadgeIcon = opts.badgeIcon;
    return (
    <div className="p-3.5 space-y-3">
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <p
            className="text-sm font-semibold leading-snug line-clamp-2"
            data-testid={opts.primary ? `text-video-express-name-${id}` : `text-video-express-name-${id}-${opts.suffix}`}
          >
            {name}
          </p>
          {metaLine}
        </div>
        <Badge
          variant="outline"
          className={cn("gap-1 text-[11px] font-medium shrink-0", opts.badgeClassName)}
          data-testid={opts.primary ? `badge-video-express-stage-${id}` : `badge-video-express-stage-${id}-${opts.suffix}`}
        >
          {BadgeIcon && <BadgeIcon className={cn("h-3 w-3", opts.badgeIconSpin && "animate-spin")} />}
          {opts.badgeLabel}
        </Badge>
        <button
          type="button"
          className="h-7 w-7 flex items-center justify-center rounded-md text-muted-foreground/30 opacity-0 group-hover:opacity-100 hover:text-destructive hover:bg-destructive/10 transition-all shrink-0"
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); onDelete(); }}
          data-testid={opts.primary ? `button-delete-video-express-${id}` : `button-delete-video-express-${id}-${opts.suffix}`}
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </div>
      {opts.body}
    </div>
    );
  };

  const tiles: React.ReactNode[] = [];

  // One card per finished video — the media-forward "card" the page is built around.
  finalVideos.forEach((v, i) => {
    const primary = tiles.length === 0;
    const saved = savedRenderIds.has(v.renderId);
    const saving = saveMutation.isPending && saveMutation.variables === v.renderId;
    tiles.push(
      <Card
        key={`video-${v.renderId}`}
        className="overflow-hidden rounded-2xl border border-border/50 bg-card shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-violet-500/40 hover:shadow-xl hover:shadow-violet-500/5 group"
        data-testid={primary ? `card-video-express-campaign-${id}` : `card-video-express-campaign-${id}-v${i}`}
      >
        <div
          className="aspect-[9/16] bg-black relative overflow-hidden"
          data-testid={primary ? `videos-video-express-${id}` : `videos-video-express-${id}-v${i}`}
        >
          <PlayableVideo src={v.url} testId={`video-video-express-${id}-${i}`} />
        </div>
        {renderFooter({
          primary,
          suffix: `v${i}`,
          badgeLabel: "Ready",
          badgeClassName: "border-emerald-500/30 bg-emerald-500/15 text-emerald-600 dark:text-emerald-400",
          badgeIcon: Check,
          body: (
            <div className={cn("grid gap-1.5", hasEditableClips ? "grid-cols-3" : "grid-cols-2")}>
              <Button
                variant="outline"
                size="sm"
                className="h-9 min-w-0 rounded-lg text-xs px-1.5 hover:border-violet-500/40 hover:text-violet-600 dark:hover:text-violet-400"
                onClick={() => downloadVideo(v.url, i)}
                data-testid={`button-download-video-${id}-${i}`}
              >
                <Download className="h-3.5 w-3.5 mr-1 shrink-0" /> <span className="truncate">Download</span>
              </Button>
              <Button
                variant={saved ? "secondary" : "outline"}
                size="sm"
                className="h-9 min-w-0 rounded-lg text-xs px-1.5 hover:border-violet-500/40 hover:text-violet-600 dark:hover:text-violet-400"
                disabled={saved || saving}
                onClick={() => saveMutation.mutate(v.renderId)}
                data-testid={`button-save-video-${id}-${i}`}
              >
                {saving ? (
                  <><Loader2 className="h-3.5 w-3.5 mr-1 shrink-0 animate-spin" /> <span className="truncate">Saving…</span></>
                ) : saved ? (
                  <><Check className="h-3.5 w-3.5 mr-1 shrink-0" /> <span className="truncate">Saved</span></>
                ) : (
                  <><Bookmark className="h-3.5 w-3.5 mr-1 shrink-0" /> <span className="truncate">Save</span></>
                )}
              </Button>
              {hasEditableClips && (
                <Button
                  variant="outline"
                  size="sm"
                  className="h-9 min-w-0 rounded-lg text-xs px-1.5 hover:border-violet-500/40 hover:text-violet-600 dark:hover:text-violet-400"
                  onClick={() => navigate(editHref)}
                  data-testid={`button-edit-video-${id}-${i}`}
                >
                  <Pencil className="h-3.5 w-3.5 mr-1 shrink-0" /> <span className="truncate">Edit</span>
                </Button>
              )}
            </div>
          ),
        })}
      </Card>,
    );
  });

  // While the run is still active (or nothing has rendered yet) show a status
  // card so progress is visible in the same grid as the finished videos.
  if (active || finalVideos.length === 0) {
    const primary = tiles.length === 0;
    tiles.push(
      <Card
        key="status"
        className="overflow-hidden rounded-2xl border border-border/50 bg-card shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-violet-500/40 hover:shadow-xl hover:shadow-violet-500/5 group"
        data-testid={primary ? `card-video-express-campaign-${id}` : `card-video-express-campaign-${id}-status`}
      >
        <div
          className="aspect-[9/16] w-full relative flex flex-col items-center justify-center gap-3 px-5 text-center bg-gradient-to-b from-violet-500/[0.06] via-muted/20 to-background"
          data-testid={`status-video-express-${id}`}
        >
          {progress.tone === "running" && (
            <span className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_50%_38%,hsl(var(--primary)/0.12),transparent_60%)] animate-pulse" />
          )}
          <div
            className={cn(
              "relative flex h-16 w-16 items-center justify-center rounded-full ring-1",
              progress.tone === "running" ? "bg-violet-500/10 ring-violet-500/30"
                : progress.tone === "done" ? "bg-emerald-500/10 ring-emerald-500/30"
                  : progress.tone === "warn" ? "bg-amber-500/10 ring-amber-500/30"
                    : progress.tone === "fail" ? "bg-destructive/10 ring-destructive/30"
                      : "bg-muted ring-border",
            )}
          >
            {progress.tone === "running" && (
              <span className="absolute inset-0 rounded-full bg-violet-500/20 animate-ping" />
            )}
            <StatusIcon className={`relative h-7 w-7 ${iconClass}`} />
          </div>
          <span className="relative text-sm font-semibold text-foreground">{progress.label}</span>
          {progress.tone === "running" && progress.description && (
            <span
              className="relative -mt-1 max-w-[15rem] text-[11px] leading-snug text-muted-foreground"
              data-testid={`text-video-express-stage-description-${id}`}
            >
              {progress.description}
            </span>
          )}
          <div className="relative h-1.5 w-3/4 rounded-full bg-muted overflow-hidden">
            <div
              className={`h-full rounded-full transition-all duration-500 ${TONE_BAR[progress.tone]}`}
              style={{ width: `${Math.max(6, progress.percent)}%` }}
              data-testid={`bar-video-express-progress-${id}`}
            />
          </div>
          <span className="relative text-[11px] tabular-nums text-muted-foreground">{Math.max(2, progress.percent)}%</span>
          {finalVideos.length > 0 && (
            <span className="relative text-[10px] text-muted-foreground">More videos rendering…</span>
          )}
        </div>
        {renderFooter({
          primary,
          suffix: "status",
          badgeLabel: progress.label,
          badgeClassName: TONE_PILL[progress.tone],
          badgeIcon: StatusIcon,
          badgeIconSpin: progress.tone === "running",
        })}
      </Card>,
    );
  }

  return <>{tiles}</>;
}

// ── Shared input pickers (reused from the former create wizard) ───────────────

export function VideoExpressPicker({
  icon, label, placeholder, value, onChange, options, testId, disabled,
}: {
  icon: React.ReactNode;
  label: string;
  placeholder: string;
  value: string;
  onChange: (v: string) => void;
  options: Array<{ value: string; label: string; imageUrl?: string | null; description?: string | null }>;
  testId: string;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const commandRef = useRef<HTMLDivElement>(null);
  const filled = !!value;
  const hasAvatars = options.some((o) => !!o.imageUrl);
  const selected = filled ? options.find((o) => o.value === value) : undefined;
  const selectedDescription = selected?.description?.trim() || "";
  const showSearch = options.length > 5;
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={label}
          data-testid={testId}
          disabled={disabled}
          className={[
            "group flex w-full items-center min-h-[72px] rounded-xl border px-3 py-2.5 text-sm transition-all duration-150 outline-none",
            "focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-0",
            "disabled:opacity-60 disabled:cursor-not-allowed",
            filled
              ? "border-primary/30 bg-gradient-to-b from-primary/[0.06] to-primary/[0.02]"
              : "border-border/60 bg-background/60 hover:border-primary/30 hover:bg-background hover:shadow-sm",
          ].join(" ")}
        >
          <div className="flex items-center gap-3 w-full min-w-0">
            {filled && selected?.imageUrl ? (
              <img
                src={selected.imageUrl}
                alt=""
                className="h-10 w-10 rounded-lg object-cover shrink-0 ring-1 ring-primary/20 shadow-sm"
                loading="lazy"
              />
            ) : (
              <div
                className={[
                  "h-10 w-10 rounded-lg flex items-center justify-center shrink-0 transition-colors ring-1",
                  filled
                    ? "bg-primary/15 text-primary ring-primary/20"
                    : "bg-muted/70 text-muted-foreground ring-border/60 group-hover:text-foreground",
                ].join(" ")}
              >
                {icon}
              </div>
            )}
            <div className="flex flex-col items-start min-w-0 flex-1 text-left">
              <span className="text-[10px] uppercase tracking-[0.08em] font-semibold text-muted-foreground/70 leading-none mb-1.5 flex items-center gap-1">
                {label}
                {!filled && <span className="text-muted-foreground/40">*</span>}
              </span>
              {filled && selected ? (
                <div className="flex flex-col w-full min-w-0">
                  <span className="text-sm truncate w-full leading-tight text-foreground font-semibold">
                    {selected.label}
                  </span>
                  {selectedDescription && (
                    <span className="text-[11px] text-muted-foreground/80 truncate w-full leading-tight mt-0.5">
                      {selectedDescription}
                    </span>
                  )}
                </div>
              ) : (
                <span className="text-sm truncate w-full leading-tight text-muted-foreground/60 font-normal">
                  {placeholder}
                </span>
              )}
            </div>
            <ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
          </div>
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="p-0 min-w-[var(--radix-popover-trigger-width)] w-[min(28rem,calc(100vw-2rem))]"
        onOpenAutoFocus={(e) => {
          // Without a CommandInput there is no tabbable child for Radix's
          // FocusScope, so cmdk's keydown handler never receives focus and
          // arrow/Enter navigation breaks. Focus the Command root directly.
          if (!showSearch) {
            e.preventDefault();
            commandRef.current?.focus();
          }
        }}
      >
        <Command ref={commandRef}>
          {showSearch && (
            <CommandInput placeholder={`Search ${label.toLowerCase()}…`} data-testid={`${testId}-search`} />
          )}
          <CommandList>
            <CommandEmpty>No {label.toLowerCase()} found.</CommandEmpty>
            {options.map((o, idx) => {
              const desc = o.description?.trim() || "";
              const isSelected = o.value === value;
              return (
                <CommandItem
                  key={o.value}
                  value={`${o.label} ${desc} ${idx}`}
                  onSelect={() => { onChange(o.value); setOpen(false); }}
                  className="gap-2"
                  data-testid={`${testId}-option-${o.value}`}
                >
                  {hasAvatars ? (
                    o.imageUrl ? (
                      <img src={o.imageUrl} alt="" className="h-6 w-6 rounded object-cover shrink-0 border border-border/40" loading="lazy" />
                    ) : (
                      <div className="h-6 w-6 rounded bg-muted flex items-center justify-center shrink-0 border border-border/40 text-[10px] font-semibold text-muted-foreground">
                        {o.label.trim().charAt(0).toUpperCase() || "?"}
                      </div>
                    )
                  ) : null}
                  <div className="flex flex-col min-w-0 flex-1">
                    <span className="text-xs whitespace-normal break-words leading-tight">{o.label}</span>
                    {desc && (
                      <span className="text-[11px] text-muted-foreground/80 truncate leading-tight mt-0.5">{desc}</span>
                    )}
                  </div>
                  {isSelected && <Check className="h-4 w-4 shrink-0 text-primary" />}
                </CommandItem>
              );
            })}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

// How many cards a family group shows before collapsing behind "View More".
const STORY_CATEGORY_COLLAPSED_COUNT = 6;

export type StoryCategoryOption = {
  value: string;
  label: string;
  imageUrl?: string | null;
  family?: string | null;
  description?: string | null;
};

const slugifyStoryCategory = (s: string) =>
  s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "") || "item";

/**
 * Visual story-category picker — image cards grouped under family headers,
 * searchable, "View More" collapse, single-select. The selected category NAME is
 * sent through, preserving the existing `videoExpressCampaigns.storyCategory`
 * contract.
 */
export function StoryCategoryGallery({
  options, value, onChange,
}: {
  options: StoryCategoryOption[];
  value: string;
  onChange: (v: string) => void;
}) {
  const [search, setSearch] = useState("");
  const [expandedFamilies, setExpandedFamilies] = useState<Set<string>>(new Set());

  const q = search.trim().toLowerCase();
  const filtered = useMemo(
    () =>
      options.filter((o) => {
        if (!q) return true;
        return (
          o.label.toLowerCase().includes(q) ||
          (o.family || "").toLowerCase().includes(q) ||
          (o.description || "").toLowerCase().includes(q)
        );
      }),
    [options, q],
  );

  const groups = useMemo(() => {
    const map = new Map<string, StoryCategoryOption[]>();
    for (const o of filtered) {
      const family = (o.family || "").trim() || "Other";
      if (!map.has(family)) map.set(family, []);
      map.get(family)!.push(o);
    }
    return Array.from(map.entries()).map(([family, items]) => ({ family, items }));
  }, [filtered]);

  const toggleFamily = (family: string) =>
    setExpandedFamilies((prev) => {
      const next = new Set(prev);
      if (next.has(family)) next.delete(family);
      else next.add(family);
      return next;
    });

  return (
    <div className="space-y-4">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground/60 pointer-events-none" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search story categories…"
          className="pl-9 h-9"
          data-testid="input-story-category-search"
        />
      </div>

      {groups.length === 0 ? (
        <div className="text-center py-10 text-muted-foreground" data-testid="text-no-story-category-results">
          <Search className="h-7 w-7 mx-auto mb-2 opacity-30" />
          <p className="text-sm">No story categories match "{search.trim()}".</p>
        </div>
      ) : (
        <div className="space-y-6">
          {groups.map(({ family, items }) => {
            const collapsible = !q && items.length > STORY_CATEGORY_COLLAPSED_COUNT;
            const expanded = expandedFamilies.has(family);
            const visibleItems = collapsible && !expanded
              ? items.slice(0, STORY_CATEGORY_COLLAPSED_COUNT)
              : items;
            const hiddenCount = items.length - STORY_CATEGORY_COLLAPSED_COUNT;
            const familyKey = slugifyStoryCategory(family);
            return (
              <div key={family} data-testid={`section-story-family-${familyKey}`}>
                <div className="flex items-center justify-between gap-3 mb-3">
                  <div className="flex items-center gap-2.5 min-w-0">
                    <div className="h-7 w-7 rounded-lg flex items-center justify-center shrink-0 bg-primary/10 text-primary">
                      <BookOpen className="h-3.5 w-3.5" />
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="text-sm font-bold tracking-tight" data-testid={`text-story-family-label-${familyKey}`}>
                          {family}
                        </h3>
                        <Badge variant="secondary" className="h-5 px-1.5 text-[10px] font-bold tabular-nums" data-testid={`badge-story-family-count-${familyKey}`}>
                          {items.length}
                        </Badge>
                      </div>
                    </div>
                  </div>
                  <div className="hidden sm:block h-px flex-1 ml-3 opacity-30 bg-primary" />
                </div>
                <div className="grid grid-cols-2 lg:grid-cols-3 gap-2">
                  {visibleItems.map((o) => (
                    <StoryCategoryCard
                      key={o.value}
                      option={o}
                      selected={o.value === value}
                      onSelect={() => onChange(o.value)}
                    />
                  ))}
                </div>
                {collapsible && (
                  <div className="mt-2.5 flex justify-center">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-7 gap-1 text-xs text-muted-foreground hover:text-foreground"
                      onClick={() => toggleFamily(family)}
                      data-testid={`button-story-family-toggle-${familyKey}`}
                    >
                      {expanded ? (
                        <>View Less<ChevronUp className="h-3.5 w-3.5" /></>
                      ) : (
                        <>View More (+{hiddenCount})<ChevronDown className="h-3.5 w-3.5" /></>
                      )}
                    </Button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function StoryCategoryCard({
  option, selected, onSelect,
}: {
  option: StoryCategoryOption;
  selected: boolean;
  onSelect: () => void;
}) {
  const slug = slugifyStoryCategory(option.value);
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={selected}
      className={[
        "group relative flex items-center gap-2.5 rounded-lg border-2 p-2 text-left transition-all cursor-pointer",
        selected
          ? "border-primary ring-2 ring-primary/30 bg-primary/5 shadow-sm"
          : "border-border/50 hover:border-border hover:shadow-sm",
      ].join(" ")}
      data-testid={`card-story-category-${slug}`}
    >
      <div className="h-10 w-10 shrink-0 rounded-md bg-muted/30 relative overflow-hidden">
        {option.imageUrl ? (
          <img
            src={option.imageUrl}
            alt={option.label}
            loading="lazy"
            className="w-full h-full object-cover transition-transform group-hover:scale-[1.04]"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <BookOpen className="h-4 w-4 text-muted-foreground/30" />
          </div>
        )}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-start gap-1.5">
          <div className="flex-1 text-[12.5px] font-semibold leading-tight tracking-tight line-clamp-1" data-testid={`text-story-category-${slug}`}>
            {option.label}
          </div>
          {selected && (
            <div className="h-5 w-5 shrink-0 rounded-full bg-primary text-primary-foreground flex items-center justify-center shadow" data-testid={`indicator-story-checked-${slug}`}>
              <Check className="h-3 w-3" strokeWidth={3} />
            </div>
          )}
        </div>
        {option.description && (
          <p className="mt-0.5 text-[11px] leading-snug text-muted-foreground">
            {option.description}
          </p>
        )}
      </div>
    </button>
  );
}
